import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, Maximize, Move, Layers, MousePointer2, Component, RefreshCw, Download, Ruler, Info, ChevronRight, Eye, EyeOff, Hash, Square, Circle, Type as TypeIcon, BoxSelect, Crosshair } from 'lucide-react';

// ═══════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════

interface CadViewerProps {
    file: { name: string; size?: number; modified?: string; path?: string };
    onClose: () => void;
    isDark: boolean;
    /** 수량 분석 결과 (Room 오버레이용) */
    analysisResult?: {
        quantities?: Array<{
            roomTag: string;
            specCode: string;
            category: string;
            quantity: number;
            unit: string;
            formula?: string;
            layerName: string;
        }>;
    } | null;
}

interface Point2D { x: number; y: number }

interface DxfEntity {
    type: string;
    layer?: string;
    color?: number;
    vertices?: Point2D[];
    center?: Point2D;
    radius?: number;
    startPoint?: Point2D;
    endPoint?: Point2D;
    startAngle?: number;
    endAngle?: number;
    text?: string;
    startPos?: Point2D;
    textHeight?: number;
    shape?: boolean;
    closed?: boolean;
    height?: number;
    blockName?: string;
    /** 고유 ID (선택용) */
    id: number;
    /** 도형의 bounding box (선택 hit-test용) */
    bbox?: { minX: number; minY: number; maxX: number; maxY: number };
}

interface DxfLayer {
    name: string;
    color: number;
    visible: boolean;
    entityCount: number;
    /** BOQ 마감 레이어 여부 */
    isFinishLayer?: boolean;
    /** 레이어 분류 */
    category?: string;
}

interface SelectedEntity {
    entity: DxfEntity;
    worldPos: Point2D;
}

interface MeasureState {
    startPoint: Point2D | null;
    endPoint: Point2D | null;
    distance: number | null;
}

// ═══════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════

const AUTOCAD_COLORS: Record<number, string> = {
    0: '#FFFFFF', 1: '#FF0000', 2: '#FFFF00', 3: '#00FF00',
    4: '#00FFFF', 5: '#0000FF', 6: '#FF00FF', 7: '#FFFFFF',
    8: '#808080', 9: '#C0C0C0', 10: '#FF0000', 11: '#FF7F7F',
    20: '#FF6666', 30: '#FF3333', 40: '#CC0000', 50: '#990000',
    251: '#333333', 252: '#555555', 253: '#777777', 254: '#999999',
    255: '#BBBBBB',
};

/** BOQ 마감 레이어 접두사 → 카테고리 매핑 */
const FINISH_LAYER_PREFIXES: [string, string][] = [
    ['FIN-FLR', '바닥공사'],
    ['FIN-WAL', '벽체공사'],
    ['FIN-CEL', '천장공사'],
    ['FIN-SEP', '재료분리대'],
    ['LGT-DL', '조명(포인트)'],
    ['LGT-LIN', '조명(라인)'],
    ['DR-', '문/창'],
    ['AR-', '건축'],
    ['ST-', '구조'],
];

/** 엔티티 타입별 아이콘 이름 (표시용) */
const ENTITY_TYPE_LABELS: Record<string, string> = {
    'LINE': '선분 (LINE)',
    'CIRCLE': '원 (CIRCLE)',
    'ARC': '호 (ARC)',
    'POLYLINE': '폴리라인',
    'LWPOLYLINE': '폴리라인',
    'HATCH': '해치 (HATCH)',
    'TEXT': '텍스트 (TEXT)',
    'MTEXT': '텍스트 (MTEXT)',
    'INSERT': '블록 삽입 (INSERT)',
    'DIMENSION': '치수 (DIMENSION)',
    'ELLIPSE': '타원 (ELLIPSE)',
};

function getACIColor(colorIndex: number): string {
    return AUTOCAD_COLORS[colorIndex] || '#CCCCCC';
}

/** 레이어가 BOQ 마감 레이어인지 판별 */
function classifyLayerCategory(layerName: string): { isFinish: boolean; category: string } {
    const upper = layerName.toUpperCase();
    for (const [prefix, cat] of FINISH_LAYER_PREFIXES) {
        if (upper.startsWith(prefix)) {
            return { isFinish: true, category: cat };
        }
    }
    return { isFinish: false, category: '' };
}

/** 엔티티 bounding box 계산 */
function computeBbox(e: DxfEntity): { minX: number; minY: number; maxX: number; maxY: number } | undefined {
    const points: Point2D[] = [];
    if (e.startPoint) points.push(e.startPoint);
    if (e.endPoint) points.push(e.endPoint);
    if (e.center) points.push(e.center);
    if (e.startPos) points.push(e.startPos);
    if (e.vertices) points.push(...e.vertices);

    if (e.radius && e.center) {
        points.push(
            { x: e.center.x - e.radius, y: e.center.y - e.radius },
            { x: e.center.x + e.radius, y: e.center.y + e.radius },
        );
    }

    if (points.length === 0) return undefined;
    return {
        minX: Math.min(...points.map(p => p.x)),
        minY: Math.min(...points.map(p => p.y)),
        maxX: Math.max(...points.map(p => p.x)),
        maxY: Math.max(...points.map(p => p.y)),
    };
}

// ═══════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════

export function CadViewer({ file, onClose, isDark, analysisResult }: CadViewerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // ── Viewport state ──
    const [scale, setScale] = useState(1);
    const [pos, setPos] = useState<Point2D>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<Point2D>({ x: 0, y: 0 });

    // ── Tools ──
    const [activeTool, setActiveTool] = useState<'pan' | 'select' | 'measure'>('pan');
    const [measure, setMeasure] = useState<MeasureState>({ startPoint: null, endPoint: null, distance: null });

    // ── Overlay modes ──
    const [showRoomOverlay, setShowRoomOverlay] = useState(false);

    // ── Data ──
    const [layerList, setLayerList] = useState<DxfLayer[]>([]);
    const [showLayers, setShowLayers] = useState(false);
    const [entities, setEntities] = useState<DxfEntity[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState({ entities: 0, layers: 0, bounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 } });
    const [mouseCoord, setMouseCoord] = useState<Point2D>({ x: 0, y: 0 });

    // ── Selection ──
    const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
    const [showProperties, setShowProperties] = useState(false);

    // ── Layer filter ──
    const [layerSearch, setLayerSearch] = useState('');
    const [showOnlyFinish, setShowOnlyFinish] = useState(false);

    // ── Entity ID counter ──
    const entityIdCounter = useRef(0);

    // ═══════════════════════════════════════
    // Load DXF
    // ═══════════════════════════════════════

    useEffect(() => {
        loadDxfFile();
    }, [file.path]);

    const loadDxfFile = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSelectedEntity(null);
        entityIdCounter.current = 0;

        const filePath = file.path || file.name;
        const ext = filePath.split('.').pop()?.toLowerCase() || '';

        try {
            const proxyRes = await fetch(`/api/cad-file?path=${encodeURIComponent(filePath)}`, {
                signal: AbortSignal.timeout(30000),
            });

            if (proxyRes.ok) {
                const contentType = proxyRes.headers.get('content-type') || '';
                if (contentType.includes('text/plain') || ext === 'dxf' || ext === 'dxf2') {
                    const text = await proxyRes.text();
                    if (text.includes('SECTION') || text.includes('ENTITIES') || text.includes('EOF')) {
                        parseDxfContent(text);
                        return;
                    }
                }
                if (contentType.includes('application/json')) {
                    const data = await proxyRes.json();
                    if (data.error) {
                        setError(`${data.message || data.error}`);
                        loadEmptyState();
                        return;
                    }
                }
            }

            if (proxyRes.status === 415 && ext !== 'dwg') {
                const data = await proxyRes.json();
                setError(`바이너리 포맷 — ${data.solutions?.[0] || 'DXF 변환 필요'}`);
                loadEmptyState();
                return;
            }

            if (ext === 'dwg') {
                try {
                    const nasGateway = 'https://grid.snapcodex.com';
                    const b64Res = await fetch(
                        `${nasGateway}/api/file-preview?path=${encodeURIComponent(filePath)}&raw=1`,
                        { signal: AbortSignal.timeout(60000) }
                    );
                    if (b64Res.ok) {
                        const json = await b64Res.json();
                        if (json.type === 'cad-binary' && json.data) {
                            const binaryStr = atob(json.data);
                            const bytes = new Uint8Array(binaryStr.length);
                            for (let i = 0; i < binaryStr.length; i++) {
                                bytes[i] = binaryStr.charCodeAt(i);
                            }
                            await parseDwgWasm(bytes);
                            return;
                        }
                    }
                    throw new Error('DWG 바이너리 다운로드 실패');
                } catch (err: any) {
                    setError(`DWG 파싱 실패: ${err.message}`);
                    loadEmptyState();
                    return;
                }
            }

            const nasGateway = 'https://grid.snapcodex.com';
            const previewRes = await fetch(`${nasGateway}/api/file-preview?path=${encodeURIComponent(filePath)}`, {
                signal: AbortSignal.timeout(15000),
            });
            if (previewRes.ok) {
                const data = await previewRes.json();
                const content = data.content || '';
                const isBinary = data.binary || data.type === 'cad';
                if (!isBinary && content && (content.includes('SECTION') || content.includes('ENTITIES'))) {
                    parseDxfContent(content);
                    return;
                }
            }

            if (ext === 'dxf' || ext === 'dxf2') {
                const rawRes = await fetch(`${nasGateway}/api/nas-raw?path=${encodeURIComponent(filePath)}`, {
                    signal: AbortSignal.timeout(30000),
                });
                if (rawRes.ok) {
                    parseDxfContent(await rawRes.text());
                    return;
                }
            }

            throw new Error('모든 로드 전략 실패');
        } catch (err: any) {
            setError(err.message || 'CAD 파일 로드 실패');
            loadEmptyState();
        }
    }, [file.path, file.name]);

    /** 엔티티에 ID와 bbox 추가 */
    function assignMeta(parsed: DxfEntity[]): DxfEntity[] {
        return parsed.map(e => {
            e.id = entityIdCounter.current++;
            e.bbox = computeBbox(e);
            return e;
        });
    }

    // ═══════════════════════════════════════
    // DXF Parsing
    // ═══════════════════════════════════════

    const parseDxfContent = useCallback(async (content: string) => {
        try {
            const DxfParser = (await import('dxf-parser')).default;
            const parser = new DxfParser();
            const dxf = parser.parseSync(content);
            if (!dxf) throw new Error('DXF 파싱 실패');

            const parsedEntities: DxfEntity[] = [];
            const layerMap = new Map<string, DxfLayer>();

            if (dxf.tables?.layer?.layers) {
                Object.values(dxf.tables.layer.layers).forEach((l: any) => {
                    const cls = classifyLayerCategory(l.name);
                    layerMap.set(l.name, {
                        name: l.name, color: l.color || 7, visible: true, entityCount: 0,
                        isFinishLayer: cls.isFinish, category: cls.category,
                    });
                });
            }

            (dxf.entities || []).forEach((e: any) => {
                const layerName = e.layer || '0';
                if (!layerMap.has(layerName)) {
                    const cls = classifyLayerCategory(layerName);
                    layerMap.set(layerName, {
                        name: layerName, color: e.color || 7, visible: true, entityCount: 0,
                        isFinishLayer: cls.isFinish, category: cls.category,
                    });
                }
                layerMap.get(layerName)!.entityCount++;

                switch (e.type) {
                    case 'LINE':
                        parsedEntities.push({ type: 'LINE', layer: layerName, color: e.color || layerMap.get(layerName)!.color, startPoint: { x: e.vertices?.[0]?.x ?? 0, y: e.vertices?.[0]?.y ?? 0 }, endPoint: { x: e.vertices?.[1]?.x ?? 0, y: e.vertices?.[1]?.y ?? 0 }, id: 0 });
                        break;
                    case 'CIRCLE':
                        parsedEntities.push({ type: 'CIRCLE', layer: layerName, color: e.color || layerMap.get(layerName)!.color, center: { x: e.center?.x ?? 0, y: e.center?.y ?? 0 }, radius: e.radius || 0, id: 0 });
                        break;
                    case 'ARC':
                        parsedEntities.push({ type: 'ARC', layer: layerName, color: e.color || layerMap.get(layerName)!.color, center: { x: e.center?.x ?? 0, y: e.center?.y ?? 0 }, radius: e.radius || 0, startAngle: (e.startAngle || 0) * Math.PI / 180, endAngle: (e.endAngle || 360) * Math.PI / 180, id: 0 });
                        break;
                    case 'LWPOLYLINE':
                    case 'POLYLINE':
                        parsedEntities.push({ type: 'POLYLINE', layer: layerName, color: e.color || layerMap.get(layerName)!.color, vertices: (e.vertices || []).map((v: any) => ({ x: v.x ?? 0, y: v.y ?? 0 })), shape: e.shape || false, id: 0 });
                        break;
                    case 'TEXT':
                    case 'MTEXT':
                        parsedEntities.push({ type: 'TEXT', layer: layerName, color: e.color || layerMap.get(layerName)!.color, text: e.text || '', startPos: { x: e.startPoint?.x ?? e.position?.x ?? 0, y: e.startPoint?.y ?? e.position?.y ?? 0 }, textHeight: e.textHeight || e.height || 100, id: 0 });
                        break;
                    case 'INSERT':
                        parsedEntities.push({ type: 'INSERT', layer: layerName, color: e.color || layerMap.get(layerName)!.color, startPos: { x: e.position?.x ?? 0, y: e.position?.y ?? 0 }, text: e.name || 'BLOCK', blockName: e.name || '', id: 0 });
                        break;
                    case 'DIMENSION':
                        parsedEntities.push({ type: 'DIMENSION', layer: layerName, color: e.color || 1, startPoint: { x: e.anchorPoint?.x ?? 0, y: e.anchorPoint?.y ?? 0 }, text: e.text || '', id: 0 });
                        break;
                    case 'HATCH':
                        if (e.boundary?.polylines) {
                            e.boundary.polylines.forEach((pl: any) => {
                                parsedEntities.push({ type: 'HATCH', layer: layerName, color: e.color || layerMap.get(layerName)!.color, vertices: (pl.vertices || []).map((v: any) => ({ x: v.x ?? 0, y: v.y ?? 0 })), id: 0 });
                            });
                        }
                        break;
                    default:
                        if (e.position || e.center || e.startPoint) {
                            parsedEntities.push({ type: e.type, layer: layerName, color: e.color || layerMap.get(layerName)!.color, startPos: { x: e.position?.x ?? e.center?.x ?? e.startPoint?.x ?? 0, y: e.position?.y ?? e.center?.y ?? e.startPoint?.y ?? 0 }, id: 0 });
                        }
                }
            });

            const finalEntities = assignMeta(parsedEntities);
            setEntities(finalEntities);
            setLayerList(Array.from(layerMap.values()));

            const bounds = calcBounds(finalEntities);
            setStats({ entities: finalEntities.length, layers: layerMap.size, bounds });
            setLoading(false);
            setTimeout(() => fitToView(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY), 100);
        } catch (err: any) {
            setError(`DXF 파싱 오류: ${err.message}`);
            loadEmptyState();
        }
    }, []);

    const parseDwgWasm = useCallback(async (data: Uint8Array) => {
        try {
            // @ts-ignore
            const libredwgModule = await import(/* webpackIgnore: true */ '/wasm/libredwg-bundle.js') as any;
            const LibreDwg = libredwgModule.LibreDwg || libredwgModule.default?.LibreDwg;
            const Dwg_File_Type = libredwgModule.Dwg_File_Type || libredwgModule.default?.Dwg_File_Type;

            const libredwg = await LibreDwg.create('/wasm/');
            const dwgData = libredwg.dwg_read_data(data.buffer, Dwg_File_Type.DWG);
            const db = libredwg.convert(dwgData);

            const parsedEntities: DxfEntity[] = [];
            const layerMap = new Map<string, DxfLayer>();

            if (db.tables?.LAYER?.entries) {
                db.tables.LAYER.entries.forEach((l: any) => {
                    const cls = classifyLayerCategory(l.name);
                    layerMap.set(l.name, { name: l.name, color: l.color ?? 7, visible: true, entityCount: 0, isFinishLayer: cls.isFinish, category: cls.category });
                });
            }

            (db.entities || []).forEach((e: any) => {
                const layerName = e.layer || '0';
                if (!layerMap.has(layerName)) {
                    const cls = classifyLayerCategory(layerName);
                    layerMap.set(layerName, { name: layerName, color: e.color ?? 7, visible: true, entityCount: 0, isFinishLayer: cls.isFinish, category: cls.category });
                }
                layerMap.get(layerName)!.entityCount++;

                const entityType = (e.type || '').toUpperCase();
                const color = e.color ?? layerMap.get(layerName)!.color ?? 7;

                switch (entityType) {
                    case 'LINE':
                        if (e.start && e.end) parsedEntities.push({ type: 'LINE', layer: layerName, color, startPoint: { x: e.start.x ?? 0, y: e.start.y ?? 0 }, endPoint: { x: e.end.x ?? 0, y: e.end.y ?? 0 }, id: 0 });
                        break;
                    case 'CIRCLE':
                        if (e.center) parsedEntities.push({ type: 'CIRCLE', layer: layerName, color, center: { x: e.center.x ?? 0, y: e.center.y ?? 0 }, radius: e.radius ?? 0, id: 0 });
                        break;
                    case 'ARC':
                        if (e.center) parsedEntities.push({ type: 'ARC', layer: layerName, color, center: { x: e.center.x ?? 0, y: e.center.y ?? 0 }, radius: e.radius ?? 0, startAngle: e.startAngle ?? 0, endAngle: e.endAngle ?? Math.PI * 2, id: 0 });
                        break;
                    case 'LWPOLYLINE': case 'POLYLINE':
                        if (e.vertices?.length) parsedEntities.push({ type: 'POLYLINE', layer: layerName, color, vertices: e.vertices.map((v: any) => ({ x: v.x ?? 0, y: v.y ?? 0 })), closed: e.flag === 1 || e.closed, id: 0 });
                        break;
                    case 'TEXT': case 'MTEXT':
                        if (e.insertionPoint || e.position) {
                            const p = e.insertionPoint || e.position;
                            parsedEntities.push({ type: 'TEXT', layer: layerName, color, startPos: { x: p.x ?? 0, y: p.y ?? 0 }, text: e.text || e.value || '', textHeight: e.height ?? 100, id: 0 });
                        }
                        break;
                    case 'INSERT':
                        if (e.insertionPoint || e.position) {
                            const p = e.insertionPoint || e.position;
                            parsedEntities.push({ type: 'INSERT', layer: layerName, color, startPos: { x: p.x ?? 0, y: p.y ?? 0 }, blockName: e.name || e.blockName || '', text: e.name || '', id: 0 });
                        }
                        break;
                    case 'DIMENSION':
                        parsedEntities.push({ type: 'DIMENSION', layer: layerName, color, startPoint: { x: e.defPoint?.x ?? 0, y: e.defPoint?.y ?? 0 }, endPoint: { x: e.defPoint2?.x ?? 0, y: e.defPoint2?.y ?? 0 }, text: e.text || '', id: 0 });
                        break;
                    case 'ELLIPSE':
                        if (e.center) parsedEntities.push({ type: 'ARC', layer: layerName, color, center: { x: e.center.x ?? 0, y: e.center.y ?? 0 }, radius: Math.sqrt((e.majorAxis?.x ?? 1) ** 2 + (e.majorAxis?.y ?? 0) ** 2), startAngle: e.startAngle ?? 0, endAngle: e.endAngle ?? Math.PI * 2, id: 0 });
                        break;
                    default:
                        const p = e.insertionPoint || e.position || e.center || e.start;
                        if (p) parsedEntities.push({ type: entityType, layer: layerName, color, startPos: { x: p.x ?? 0, y: p.y ?? 0 }, id: 0 });
                }
            });

            const finalEntities = assignMeta(parsedEntities);
            setEntities(finalEntities);
            setLayerList(Array.from(layerMap.values()));

            const bounds = calcBounds(finalEntities);
            setStats({ entities: finalEntities.length, layers: layerMap.size, bounds });
            setLoading(false);
            setTimeout(() => fitToView(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY), 100);
        } catch (err: any) {
            throw new Error(`WASM 파싱 실패: ${err.message}`);
        }
    }, []);

    const loadEmptyState = useCallback(() => {
        setEntities([]);
        setLayerList([]);
        setStats({ entities: 0, layers: 0, bounds: { minX: 0, minY: 0, maxX: 1000, maxY: 1000 } });
        setLoading(false);
    }, []);

    // ═══════════════════════════════════════
    // Utilities
    // ═══════════════════════════════════════

    function calcBounds(ents: DxfEntity[]) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        ents.forEach(e => {
            const pts = [e.startPoint, e.endPoint, e.center, e.startPos, ...(e.vertices || [])].filter(Boolean) as Point2D[];
            pts.forEach(p => {
                if (p.x < minX) minX = p.x;
                if (p.y < minY) minY = p.y;
                if (p.x > maxX) maxX = p.x;
                if (p.y > maxY) maxY = p.y;
            });
        });
        if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 10000; maxY = 10000; }
        return { minX, minY, maxX, maxY };
    }

    const fitToView = useCallback((minX: number, minY: number, maxX: number, maxY: number) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const drawingW = maxX - minX || 1;
        const drawingH = maxY - minY || 1;
        const scaleX = (canvas.width * 0.85) / drawingW;
        const scaleY = (canvas.height * 0.85) / drawingH;
        const fitScale = Math.min(scaleX, scaleY);
        setScale(fitScale);
        setPos({
            x: canvas.width / 2 - (minX + drawingW / 2) * fitScale,
            y: canvas.height / 2 + (minY + drawingH / 2) * fitScale,
        });
    }, []);

    /** 캔버스 좌표 → 월드 좌표 */
    function canvasToWorld(cx: number, cy: number): Point2D {
        return {
            x: (cx - pos.x) / scale,
            y: -(cy - pos.y) / scale,
        };
    }

    /** 월드 좌표 → 캔버스 좌표 */
    function worldToCanvas(wx: number, wy: number): Point2D {
        return {
            x: wx * scale + pos.x,
            y: -wy * scale + pos.y,
        };
    }

    /** Hit-test: 클릭 위치에서 가장 가까운 엔티티 찾기 */
    function findEntityAtPoint(worldPt: Point2D, tolerance: number = 500): DxfEntity | null {
        let bestEntity: DxfEntity | null = null;
        let bestDist = tolerance;

        const visibleLayers = new Set(layerList.filter(l => l.visible).map(l => l.name));

        for (const e of entities) {
            if (e.layer && !visibleLayers.has(e.layer)) continue;

            const dist = distanceToEntity(worldPt, e);
            if (dist < bestDist) {
                bestDist = dist;
                bestEntity = e;
            }
        }

        return bestEntity;
    }

    /** 점과 엔티티 사이의 최소 거리 */
    function distanceToEntity(pt: Point2D, e: DxfEntity): number {
        switch (e.type) {
            case 'LINE':
                if (e.startPoint && e.endPoint) {
                    return distToSegment(pt, e.startPoint, e.endPoint);
                }
                break;
            case 'CIRCLE':
                if (e.center && e.radius) {
                    return Math.abs(Math.hypot(pt.x - e.center.x, pt.y - e.center.y) - e.radius);
                }
                break;
            case 'ARC':
                if (e.center && e.radius) {
                    const d = Math.hypot(pt.x - e.center.x, pt.y - e.center.y);
                    const angle = Math.atan2(pt.y - e.center.y, pt.x - e.center.x);
                    if (isAngleInRange(angle, e.startAngle || 0, e.endAngle || Math.PI * 2)) {
                        return Math.abs(d - e.radius);
                    }
                }
                break;
            case 'POLYLINE':
            case 'HATCH':
                if (e.vertices && e.vertices.length > 1) {
                    let minDist = Infinity;
                    for (let i = 0; i < e.vertices.length - 1; i++) {
                        const d = distToSegment(pt, e.vertices[i], e.vertices[i + 1]);
                        if (d < minDist) minDist = d;
                    }
                    if (e.shape && e.vertices.length > 2) {
                        const d = distToSegment(pt, e.vertices[e.vertices.length - 1], e.vertices[0]);
                        if (d < minDist) minDist = d;
                    }
                    return minDist;
                }
                break;
            case 'TEXT':
            case 'MTEXT':
            case 'INSERT':
                if (e.startPos) {
                    return Math.hypot(pt.x - e.startPos.x, pt.y - e.startPos.y);
                }
                break;
            case 'DIMENSION':
                if (e.startPoint) {
                    return Math.hypot(pt.x - e.startPoint.x, pt.y - e.startPoint.y);
                }
                break;
        }
        if (e.bbox) {
            if (pt.x >= e.bbox.minX && pt.x <= e.bbox.maxX && pt.y >= e.bbox.minY && pt.y <= e.bbox.maxY) {
                return Math.min(pt.x - e.bbox.minX, e.bbox.maxX - pt.x, pt.y - e.bbox.minY, e.bbox.maxY - pt.y);
            }
        }
        return Infinity;
    }

    function distToSegment(p: Point2D, a: Point2D, b: Point2D): number {
        const dx = b.x - a.x, dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
        let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
    }

    function isAngleInRange(angle: number, start: number, end: number): boolean {
        // Normalize to [0, 2π)
        const norm = (a: number) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const na = norm(angle), ns = norm(start), ne = norm(end);
        if (ns <= ne) return na >= ns && na <= ne;
        return na >= ns || na <= ne;
    }

    // ═══════════════════════════════════════
    // Rendering
    // ═══════════════════════════════════════

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || entities.length === 0) {
            // 빈 캔버스도 렌더
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = '#0a0a14';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
            }
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const W = canvas.width, H = canvas.height;

        // Background
        ctx.fillStyle = '#0a0a14';
        ctx.fillRect(0, 0, W, H);

        ctx.save();
        ctx.translate(pos.x, pos.y);
        ctx.scale(scale, -scale);

        // Grid
        const gridSize = scale > 0.01 ? 1000 : 5000;
        ctx.strokeStyle = '#141420';
        ctx.lineWidth = 0.5 / scale;
        const gMinX = stats.bounds.minX - 2000;
        const gMaxX = stats.bounds.maxX + 2000;
        const gMinY = stats.bounds.minY - 2000;
        const gMaxY = stats.bounds.maxY + 2000;
        ctx.beginPath();
        for (let x = Math.floor(gMinX / gridSize) * gridSize; x <= gMaxX; x += gridSize) { ctx.moveTo(x, gMinY); ctx.lineTo(x, gMaxY); }
        for (let y = Math.floor(gMinY / gridSize) * gridSize; y <= gMaxY; y += gridSize) { ctx.moveTo(gMinX, y); ctx.lineTo(gMaxX, y); }
        ctx.stroke();

        // Origin cross
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 1 / scale;
        ctx.beginPath();
        ctx.moveTo(-500, 0); ctx.lineTo(500, 0);
        ctx.moveTo(0, -500); ctx.lineTo(0, 500);
        ctx.stroke();

        // Visible layers
        const visibleLayers = new Set(layerList.filter(l => l.visible).map(l => l.name));

        // Viewport bounds
        const vpMinX = -pos.x / scale;
        const vpMaxX = (W - pos.x) / scale;
        const vpMinY = -(H - pos.y) / scale;
        const vpMaxY = pos.y / scale;
        const margin = 2000 / scale;

        // LOD
        const showText = scale > 0.003;
        const showInsert = scale > 0.002;

        let rendered = 0;
        const MAX_RENDER = 60000;
        const selectedId = selectedEntity?.entity.id;

        // ── Pass 1: Render all entities ──
        entities.forEach(e => {
            if (rendered >= MAX_RENDER) return;
            if (e.layer && !visibleLayers.has(e.layer)) return;

            const pt = e.startPoint || e.center || e.startPos || e.vertices?.[0];
            if (pt) {
                const ex = pt.x, ey = pt.y;
                const r = e.radius || 500;
                if (ex + r < vpMinX - margin || ex - r > vpMaxX + margin ||
                    ey + r < vpMinY - margin || ey - r > vpMaxY + margin) return;
            }

            if (!showText && (e.type === 'TEXT' || e.type === 'MTEXT')) return;
            if (!showInsert && e.type === 'INSERT') return;

            const isSelected = e.id === selectedId;
            const color = getACIColor(e.color || 7);

            // 마감 레이어 하이라이트 (미세한 강조)
            const layerInfo = layerList.find(l => l.name === e.layer);
            const isFinish = layerInfo?.isFinishLayer;

            ctx.strokeStyle = isSelected ? '#10b981' : color;
            ctx.fillStyle = isSelected ? '#10b981' : color;
            ctx.lineWidth = isSelected ? (4 / scale) : (e.type === 'HATCH' ? 0.5 : isFinish ? 2 : 1.5) / scale;

            if (isSelected) {
                ctx.shadowColor = '#10b981';
                ctx.shadowBlur = 12 / scale;
            }

            rendered++;
            drawEntity(ctx, e, scale, showText);

            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        });

        // ── Pass 2: Selection highlight (선택 시 bounding box) ──
        if (selectedEntity && selectedEntity.entity.bbox) {
            const b = selectedEntity.entity.bbox;
            const pad = 200 / scale;
            ctx.strokeStyle = '#10b981';
            ctx.lineWidth = 2 / scale;
            ctx.setLineDash([200 / scale, 100 / scale]);
            ctx.strokeRect(b.minX - pad, b.minY - pad, (b.maxX - b.minX) + pad * 2, (b.maxY - b.minY) + pad * 2);
            ctx.setLineDash([]);
        }

        // ── Pass 3: Room overlay (분석 결과 기반) ──
        if (showRoomOverlay && analysisResult?.quantities && analysisResult.quantities.length > 0 && scale > 0.001) {
            // 카테고리별 컬러맵
            const categoryColors: Record<string, string> = {
                'floor': 'rgba(59,130,246,0.12)',    // blue
                'wall': 'rgba(239,68,68,0.12)',       // red
                'ceiling': 'rgba(168,85,247,0.12)',   // purple
                'separator': 'rgba(245,158,11,0.12)', // amber
                'lighting_point': 'rgba(234,179,8,0.12)', // yellow
                'lighting_linear': 'rgba(234,179,8,0.12)',
                'door': 'rgba(34,197,94,0.12)',       // green
                'window': 'rgba(6,182,212,0.12)',     // cyan
                'furniture': 'rgba(249,115,22,0.12)', // orange
            };
            const categoryBorders: Record<string, string> = {
                'floor': 'rgba(59,130,246,0.6)',
                'wall': 'rgba(239,68,68,0.6)',
                'ceiling': 'rgba(168,85,247,0.6)',
                'separator': 'rgba(245,158,11,0.6)',
                'lighting_point': 'rgba(234,179,8,0.6)',
                'lighting_linear': 'rgba(234,179,8,0.6)',
                'door': 'rgba(34,197,94,0.6)',
                'window': 'rgba(6,182,212,0.6)',
                'furniture': 'rgba(249,115,22,0.6)',
            };

            // Room TAG별로 그룹핑
            const roomMap = new Map<string, typeof analysisResult.quantities>();
            for (const q of analysisResult.quantities) {
                const tag = q.roomTag || '미지정';
                if (!roomMap.has(tag)) roomMap.set(tag, []);
                roomMap.get(tag)!.push(q);
            }

            // 각 room TAG 텍스트 엔티티 근처에 오버레이
            const textEntities = entities.filter(e => (e.type === 'TEXT' || e.type === 'MTEXT') && e.text);
            for (const [roomTag, quantities] of roomMap) {
                // 동일 room TAG 텍스트 엔티티 찾기
                const tagEntity = textEntities.find(e => e.text?.trim() === roomTag);
                if (!tagEntity?.startPos) continue;

                const rx = tagEntity.startPos.x;
                const ry = tagEntity.startPos.y;

                // 해당 실의 카테고리 목록
                const cats = [...new Set(quantities.map(q => q.category))];

                // 컬러 오버레이 배경
                for (const cat of cats) {
                    const fillColor = categoryColors[cat] || 'rgba(156,163,175,0.08)';
                    const borderColor = categoryBorders[cat] || 'rgba(156,163,175,0.3)';
                    const catItems = quantities.filter(q => q.category === cat);
                    const totalQty = catItems.reduce((s, q) => s + q.quantity, 0);
                    const unit = catItems[0]?.unit || '';

                    // room tag 주변에 라벨 배치 (Y 오프셋으로 카테고리별 분리)
                    const catIdx = cats.indexOf(cat);
                    const offsetY = -600 * (catIdx + 1);

                    ctx.save();
                    ctx.translate(rx, ry + offsetY);
                    ctx.scale(1, -1);

                    const fontSize = Math.max(200, 350 / Math.max(scale, 0.001));
                    const padding = fontSize * 0.3;

                    // 배경 박스
                    const label = `${cat === 'floor' ? '바닥' : cat === 'wall' ? '벽체' : cat === 'ceiling' ? '천장' : cat === 'lighting_point' ? '조명' : cat} ${totalQty.toFixed(1)}${unit}`;
                    ctx.font = `bold ${fontSize}px Pretendard, Inter, sans-serif`;
                    const textWidth = ctx.measureText(label).width;

                    ctx.fillStyle = fillColor;
                    ctx.fillRect(-padding, -fontSize - padding, textWidth + padding * 2, fontSize + padding * 2);
                    ctx.strokeStyle = borderColor;
                    ctx.lineWidth = 1.5 / scale;
                    ctx.strokeRect(-padding, -fontSize - padding, textWidth + padding * 2, fontSize + padding * 2);

                    // 텍스트
                    ctx.fillStyle = borderColor;
                    ctx.fillText(label, 0, -padding * 0.5);

                    ctx.restore();
                }
            }
        }

        // ── Pass 4: Measure overlay ──
        if (measure.startPoint) {
            const ms = measure.startPoint;
            const me = measure.endPoint;
            const clr = '#f59e0b';

            // 시작점 마커
            ctx.fillStyle = clr;
            ctx.beginPath();
            ctx.arc(ms.x, ms.y, 150 / scale, 0, Math.PI * 2);
            ctx.fill();

            if (me) {
                // 끝점 마커
                ctx.beginPath();
                ctx.arc(me.x, me.y, 150 / scale, 0, Math.PI * 2);
                ctx.fill();

                // 연결선
                ctx.strokeStyle = clr;
                ctx.lineWidth = 2.5 / scale;
                ctx.setLineDash([300 / scale, 150 / scale]);
                ctx.beginPath();
                ctx.moveTo(ms.x, ms.y);
                ctx.lineTo(me.x, me.y);
                ctx.stroke();
                ctx.setLineDash([]);

                // 거리 라벨
                if (measure.distance !== null) {
                    const midX = (ms.x + me.x) / 2;
                    const midY = (ms.y + me.y) / 2;
                    ctx.save();
                    ctx.translate(midX, midY);
                    ctx.scale(1, -1);
                    const fontSize = Math.max(180, 400 / Math.max(scale, 0.001));
                    ctx.font = `bold ${fontSize}px Pretendard, Inter, monospace`;
                    ctx.fillStyle = clr;
                    ctx.textAlign = 'center';
                    ctx.fillText(`${measure.distance.toFixed(1)}`, 0, -fontSize * 0.5);
                    ctx.restore();
                }
            }
        }

        ctx.restore();

        // ── Pass 5: Unmatched room detection (빨간 강조) ──
        // 닫힌 폴리라인 중 room TAG 텍스트와 매칭되지 않은 것 표시
        if (showRoomOverlay && scale > 0.001) {
            const ctx2 = canvasRef.current?.getContext('2d');
            if (ctx2) {
                // 텍스트 엔티티의 room name 목록
                const roomNames = new Set(
                    entities
                        .filter(e => (e.type === 'TEXT' || e.type === 'MTEXT') && e.text)
                        .map(e => e.text!.trim())
                        .filter(t => t.length >= 1 && t.length <= 15)
                );

                // 닫힌 폴리라인 중 텍스트 매칭 안 된 것 찾기
                const unmatchedRooms = entities.filter(e => {
                    if (e.type !== 'POLYLINE' || !e.shape || !e.vertices || e.vertices.length < 3) return false;
                    // 면적이 너무 작거나 크면 제외
                    const area = Math.abs(polygonArea(e.vertices));
                    if (area < 100000 || area > 500000000) return false; // ~1㎡ ~ ~5000㎡
                    // 내부에 room TAG 텍스트가 있는지 확인
                    const hasLabel = entities.some(t =>
                        (t.type === 'TEXT' || t.type === 'MTEXT') && t.startPos && t.text &&
                        pointInPolygon(t.startPos, e.vertices!)
                    );
                    return !hasLabel;
                });

                if (unmatchedRooms.length > 0) {
                    ctx2.save();
                    ctx2.translate(pos.x, pos.y);
                    ctx2.scale(scale, -scale);

                    for (const room of unmatchedRooms) {
                        const verts = room.vertices!;
                        // 빨간색 점선 테두리
                        ctx2.strokeStyle = 'rgba(239,68,68,0.7)';
                        ctx2.lineWidth = 3 / scale;
                        ctx2.setLineDash([500 / scale, 300 / scale]);
                        ctx2.beginPath();
                        ctx2.moveTo(verts[0].x, verts[0].y);
                        verts.slice(1).forEach(v => ctx2.lineTo(v.x, v.y));
                        ctx2.closePath();
                        ctx2.stroke();

                        // 반투명 빨간 배경
                        ctx2.fillStyle = 'rgba(239,68,68,0.06)';
                        ctx2.fill();

                        // "?" 라벨
                        const cx = verts.reduce((s, v) => s + v.x, 0) / verts.length;
                        const cy = verts.reduce((s, v) => s + v.y, 0) / verts.length;
                        ctx2.setLineDash([]);
                        ctx2.translate(cx, cy);
                        ctx2.scale(1, -1);
                        const fontSize = Math.max(300, 500 / Math.max(scale, 0.001));
                        ctx2.font = `bold ${fontSize}px Pretendard, Inter, sans-serif`;
                        ctx2.fillStyle = 'rgba(239,68,68,0.5)';
                        ctx2.textAlign = 'center';
                        ctx2.fillText('?', 0, fontSize * 0.3);
                        ctx2.scale(1, -1);
                        ctx2.translate(-cx, -cy);
                    }

                    ctx2.setLineDash([]);
                    ctx2.restore();
                }
            }
        }

        // Status overlay
        ctx.fillStyle = isDark ? '#10b981' : '#059669';
        ctx.font = 'bold 11px Inter, sans-serif';
        ctx.fillText(`${file.name} — ${stats.entities} entities, ${stats.layers} layers`, 12, 20);

        if (measure.distance !== null) {
            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 13px Inter, sans-serif';
            ctx.fillText(`측정: ${measure.distance.toFixed(1)} units`, 12, 38);
        }

    }, [entities, scale, pos, isDark, layerList, stats, file.name, selectedEntity, measure, showRoomOverlay, analysisResult]);

    function drawEntity(ctx: CanvasRenderingContext2D, e: DxfEntity, currentScale: number, showText: boolean) {
        switch (e.type) {
            case 'LINE':
                if (e.startPoint && e.endPoint) {
                    ctx.beginPath();
                    ctx.moveTo(e.startPoint.x, e.startPoint.y);
                    ctx.lineTo(e.endPoint.x, e.endPoint.y);
                    ctx.stroke();
                }
                break;
            case 'CIRCLE':
                if (e.center && e.radius) {
                    ctx.beginPath();
                    ctx.arc(e.center.x, e.center.y, e.radius, 0, Math.PI * 2);
                    ctx.stroke();
                }
                break;
            case 'ARC':
                if (e.center && e.radius) {
                    ctx.beginPath();
                    ctx.arc(e.center.x, e.center.y, e.radius, e.startAngle || 0, e.endAngle || Math.PI * 2);
                    ctx.stroke();
                }
                break;
            case 'POLYLINE':
                if (e.vertices && e.vertices.length > 1) {
                    ctx.beginPath();
                    ctx.moveTo(e.vertices[0].x, e.vertices[0].y);
                    e.vertices.slice(1).forEach(v => ctx.lineTo(v.x, v.y));
                    if (e.shape) ctx.closePath();
                    ctx.stroke();
                }
                break;
            case 'HATCH':
                if (e.vertices && e.vertices.length > 2) {
                    ctx.globalAlpha = 0.1;
                    ctx.beginPath();
                    ctx.moveTo(e.vertices[0].x, e.vertices[0].y);
                    e.vertices.slice(1).forEach(v => ctx.lineTo(v.x, v.y));
                    ctx.closePath();
                    ctx.fill();
                    ctx.globalAlpha = 1;
                }
                break;
            case 'TEXT':
            case 'MTEXT':
                if (showText && e.startPos && e.text) {
                    ctx.save();
                    ctx.translate(e.startPos.x, e.startPos.y);
                    ctx.scale(1, -1);
                    const fontSize = Math.max(e.textHeight || 100, 50);
                    ctx.font = `${fontSize}px Pretendard, Inter, monospace`;
                    ctx.fillText(e.text, 0, 0);
                    ctx.restore();
                }
                break;
            case 'INSERT':
                if (e.startPos) {
                    ctx.save();
                    ctx.translate(e.startPos.x, e.startPos.y);
                    ctx.strokeStyle = '#555';
                    ctx.strokeRect(-50, -50, 100, 100);
                    if (showText && e.text) {
                        ctx.scale(1, -1);
                        ctx.font = '60px monospace';
                        ctx.fillStyle = '#888';
                        ctx.fillText(e.text, -40, 10);
                    }
                    ctx.restore();
                }
                break;
            case 'DIMENSION':
                if (e.startPoint) {
                    ctx.save();
                    ctx.translate(e.startPoint.x, e.startPoint.y);
                    ctx.scale(1, -1);
                    ctx.font = '150px monospace';
                    ctx.fillStyle = '#FF0000';
                    ctx.fillText(e.text || '', 0, 0);
                    ctx.restore();
                }
                break;
        }
    }

    // ═══════════════════════════════════════
    // Mouse Handlers
    // ═══════════════════════════════════════

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const cx = (e.clientX - rect.left) * (canvas.width / rect.width);
        const cy = (e.clientY - rect.top) * (canvas.height / rect.height);
        const worldPt = canvasToWorld(cx, cy);

        if (activeTool === 'pan') {
            setIsDragging(true);
            setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
        } else if (activeTool === 'select') {
            const hit = findEntityAtPoint(worldPt, 600 / scale);
            if (hit) {
                setSelectedEntity({ entity: hit, worldPos: worldPt });
                setShowProperties(true);
            } else {
                setSelectedEntity(null);
                setShowProperties(false);
            }
        } else if (activeTool === 'measure') {
            if (!measure.startPoint) {
                setMeasure({ startPoint: worldPt, endPoint: null, distance: null });
            } else {
                const dist = Math.hypot(worldPt.x - measure.startPoint.x, worldPt.y - measure.startPoint.y);
                setMeasure(prev => ({ ...prev, endPoint: worldPt, distance: dist }));
            }
        }
    }, [activeTool, pos, scale, entities, layerList, measure.startPoint]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const cx = (e.clientX - rect.left) * (canvas.width / rect.width);
        const cy = (e.clientY - rect.top) * (canvas.height / rect.height);

        // Update coordinate display
        setMouseCoord(canvasToWorld(cx, cy));

        if (isDragging && activeTool === 'pan') {
            setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
        }

        // 측정 중 실시간 업데이트
        if (activeTool === 'measure' && measure.startPoint && !measure.endPoint) {
            const worldPt = canvasToWorld(cx, cy);
            const dist = Math.hypot(worldPt.x - measure.startPoint.x, worldPt.y - measure.startPoint.y);
            setMeasure(prev => ({ ...prev, endPoint: worldPt, distance: dist }));
        }
    }, [isDragging, activeTool, dragStart, measure.startPoint, measure.endPoint, pos, scale]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    /** Zoom to cursor position */
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const cx = (e.clientX - rect.left) * (canvas.width / rect.width);
        const cy = (e.clientY - rect.top) * (canvas.height / rect.height);

        const factor = e.deltaY < 0 ? 1.15 : 0.87;
        const newScale = Math.min(Math.max(scale * factor, 0.0001), 50);

        // 마우스 커서 위치 기준으로 이동 (zoom-to-cursor)
        const newX = cx - (cx - pos.x) * (newScale / scale);
        const newY = cy - (cy - pos.y) * (newScale / scale);

        setScale(newScale);
        setPos({ x: newX, y: newY });
    }, [scale, pos]);

    const toggleLayer = useCallback((name: string) => {
        setLayerList(prev => prev.map(l => l.name === name ? { ...l, visible: !l.visible } : l));
    }, []);

    /** 측정 초기화 */
    const resetMeasure = useCallback(() => {
        setMeasure({ startPoint: null, endPoint: null, distance: null });
    }, []);

    // ═══════════════════════════════════════
    // Computed
    // ═══════════════════════════════════════

    const filteredLayers = layerList.filter(l => {
        if (layerSearch && !l.name.toLowerCase().includes(layerSearch.toLowerCase())) return false;
        if (showOnlyFinish && !l.isFinishLayer) return false;
        return true;
    });

    const finishLayerCount = layerList.filter(l => l.isFinishLayer).length;

    // ═══════════════════════════════════════
    // JSX
    // ═══════════════════════════════════════

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
                style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)' }}
            >
                <motion.div
                    initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
                    className={`w-full max-w-[98vw] h-full max-h-[98vh] rounded-2xl flex flex-col overflow-hidden shadow-2xl border ${isDark ? 'bg-gray-950 border-white/10' : 'bg-white border-gray-200'}`}
                >
                    {/* ── Toolbar ── */}
                    <div className={`flex items-center justify-between px-4 py-2 border-b shrink-0 ${isDark ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center"><Component className="w-5 h-5" /></div>
                            <div>
                                <h3 className="text-sm font-bold flex items-center gap-2">
                                    {file.name}
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${error ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                        {error ? '데이터 없음' : file.name.match(/\.(dwg|dxf|dxf2)$/i)?.[1]?.toUpperCase() || 'DXF'}
                                    </span>
                                    {finishLayerCount > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-blue-500/20 text-blue-400">
                                            BOQ {finishLayerCount}레이어
                                        </span>
                                    )}
                                </h3>
                                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {file.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB · ` : ''}{stats.entities} entities · {stats.layers} layers
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* Tools */}
                            <div className={`flex items-center gap-0.5 p-1 rounded-lg mr-2 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                <button onClick={() => { setActiveTool('pan'); resetMeasure(); }} className={`p-1.5 rounded-md transition ${activeTool === 'pan' ? 'bg-emerald-500 text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`} title="이동 (Pan)"><Move className="w-4 h-4" /></button>
                                <button onClick={() => { setActiveTool('select'); resetMeasure(); }} className={`p-1.5 rounded-md transition ${activeTool === 'select' ? 'bg-emerald-500 text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`} title="선택 (Select)"><MousePointer2 className="w-4 h-4" /></button>
                                <button onClick={() => { setActiveTool('measure'); setSelectedEntity(null); setShowProperties(false); resetMeasure(); }} className={`p-1.5 rounded-md transition ${activeTool === 'measure' ? 'bg-amber-500 text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`} title="측정 (Measure)"><Ruler className="w-4 h-4" /></button>
                            </div>

                            {/* Zoom */}
                            <button onClick={() => setScale(s => Math.min(s * 1.3, 50))} className={`p-1.5 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}><ZoomIn className="w-4 h-4" /></button>
                            <span className="text-[10px] w-14 text-center font-mono opacity-60">{scale > 0.01 ? `${Math.round(scale * 100)}%` : `${(scale * 1000).toFixed(1)}‰`}</span>
                            <button onClick={() => setScale(s => Math.max(s * 0.77, 0.0001))} className={`p-1.5 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}><ZoomOut className="w-4 h-4" /></button>
                            <button onClick={() => fitToView(stats.bounds.minX, stats.bounds.minY, stats.bounds.maxX, stats.bounds.maxY)} className={`p-1.5 rounded-lg border ml-1 ${isDark ? 'border-white/10 hover:bg-white/5 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`} title="전체 보기"><Maximize className="w-4 h-4" /></button>

                            <div className="w-px h-6 bg-gray-500/20 mx-1" />

                            {/* Layer toggle */}
                            <button onClick={() => { setShowLayers(!showLayers); if (!showLayers) setShowProperties(false); }} className={`p-1.5 rounded-lg transition ${showLayers ? 'bg-emerald-500/20 text-emerald-500' : isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-black'}`} title="레이어"><Layers className="w-4 h-4" /></button>

                            {/* Room overlay toggle */}
                            <button onClick={() => setShowRoomOverlay(!showRoomOverlay)} className={`p-1.5 rounded-lg transition ${showRoomOverlay ? 'bg-blue-500/20 text-blue-500' : isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-black'}`} title="Room 오버레이"><Crosshair className="w-4 h-4" /></button>

                            {/* Properties toggle */}
                            <button onClick={() => setShowProperties(!showProperties)} className={`p-1.5 rounded-lg transition ${showProperties ? 'bg-emerald-500/20 text-emerald-500' : isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-black'}`} title="속성"><Info className="w-4 h-4" /></button>

                            {/* Reload */}
                            <button onClick={loadDxfFile} className={`p-1.5 rounded-lg ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-black'}`} title="새로고침"><RefreshCw className="w-4 h-4" /></button>

                            {/* Close */}
                            <button onClick={onClose} className="p-1.5 rounded-lg ml-1 bg-red-500/10 text-red-500 hover:bg-red-500/20 transition"><X className="w-5 h-5" /></button>
                        </div>
                    </div>

                    {/* ── Main area ── */}
                    <div className="flex-1 flex relative overflow-hidden min-h-0">
                        {/* Canvas */}
                        <div ref={containerRef} className="flex-1 relative overflow-hidden"
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{ cursor: activeTool === 'pan' ? (isDragging ? 'grabbing' : 'grab') : activeTool === 'measure' ? 'crosshair' : activeTool === 'select' ? 'pointer' : 'default' }}
                        >
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/50">
                                    <div className="flex items-center gap-3 text-white">
                                        <RefreshCw className="w-6 h-6 animate-spin" />
                                        <span className="text-sm font-bold">DXF 파일 로딩 중...</span>
                                    </div>
                                </div>
                            )}
                            <canvas
                                ref={canvasRef}
                                width={2400}
                                height={1400}
                                className="absolute top-0 left-0 w-full h-full"
                                style={{ imageRendering: 'auto' }}
                            />

                            {/* Status bar */}
                            <div className="absolute bottom-3 left-3 flex gap-2 flex-wrap">
                                <div className={`px-2 py-1 rounded text-[10px] font-mono ${isDark ? 'bg-black/60 text-emerald-400' : 'bg-white/80 text-emerald-600'}`}>
                                    X: {mouseCoord.x.toFixed(0)}, Y: {mouseCoord.y.toFixed(0)}
                                </div>
                                <div className={`px-2 py-1 rounded text-[10px] font-mono ${isDark ? 'bg-black/60 text-gray-400' : 'bg-white/80 text-gray-600'}`}>
                                    {stats.entities} entities
                                </div>
                                {measure.distance !== null && (
                                    <div className={`px-2 py-1 rounded text-[10px] font-mono font-bold ${isDark ? 'bg-amber-900/60 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
                                        <Ruler className="w-3 h-3 inline mr-1" />{measure.distance.toFixed(1)} units
                                        <button onClick={resetMeasure} className="ml-1.5 opacity-60 hover:opacity-100">✕</button>
                                    </div>
                                )}
                                {selectedEntity && (
                                    <div className={`px-2 py-1 rounded text-[10px] font-mono ${isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                        선택: {ENTITY_TYPE_LABELS[selectedEntity.entity.type] || selectedEntity.entity.type}
                                    </div>
                                )}
                                {error && (
                                    <div className={`px-2 py-1 rounded text-[10px] font-mono flex items-center gap-1 max-w-md truncate ${isDark ? 'bg-yellow-900/60 text-yellow-400' : 'bg-yellow-50 text-yellow-600'}`}>
                                        <Info className="w-3 h-3 shrink-0" /> {error}
                                    </div>
                                )}
                            </div>

                            {/* Tool hint */}
                            {activeTool === 'measure' && !measure.startPoint && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-xs font-medium">
                                    클릭하여 첫 번째 점을 지정하세요
                                </div>
                            )}
                            {activeTool === 'measure' && measure.startPoint && !measure.endPoint && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-xs font-medium">
                                    두 번째 점을 클릭하세요
                                </div>
                            )}
                            {activeTool === 'select' && !selectedEntity && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                                    엔티티를 클릭하여 선택하세요
                                </div>
                            )}
                        </div>

                        {/* ── Layer Panel ── */}
                        <AnimatePresence>
                            {showLayers && (
                                <motion.div
                                    initial={{ width: 0, opacity: 0 }} animate={{ width: 240, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
                                    className={`border-l overflow-hidden flex flex-col shrink-0 ${isDark ? 'bg-gray-900/90 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className={`px-3 py-2 border-b text-[11px] font-bold flex items-center justify-between shrink-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                        <span>레이어 ({layerList.length})</span>
                                        {finishLayerCount > 0 && (
                                            <button onClick={() => setShowOnlyFinish(!showOnlyFinish)}
                                                className={`text-[9px] px-1.5 py-0.5 rounded transition ${showOnlyFinish ? 'bg-blue-500 text-white' : isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                                BOQ {showOnlyFinish ? '전체' : '마감만'}
                                            </button>
                                        )}
                                    </div>
                                    {/* 검색 */}
                                    <div className={`px-2 py-1.5 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                        <input
                                            type="text" value={layerSearch} onChange={e => setLayerSearch(e.target.value)}
                                            placeholder="레이어 검색..."
                                            className={`w-full px-2 py-1 text-[10px] rounded border ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-600' : 'bg-white border-gray-200 text-black placeholder-gray-400'}`}
                                        />
                                    </div>
                                    {/* 레이어 리스트 */}
                                    <div className="flex-1 overflow-y-auto min-h-0">
                                        {filteredLayers.map(layer => (
                                            <button key={layer.name} onClick={() => toggleLayer(layer.name)}
                                                className={`w-full text-left px-3 py-1.5 text-[10px] flex items-center gap-2 transition ${layer.visible ? '' : 'opacity-30'} ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}>
                                                <span className={`w-3 h-3 rounded-sm shrink-0 ${layer.isFinishLayer ? 'ring-1 ring-blue-400' : ''}`} style={{ backgroundColor: getACIColor(layer.color) }} />
                                                <span className="flex-1 truncate font-mono">{layer.name}</span>
                                                <span className={`text-[8px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{layer.entityCount}</span>
                                                {!layer.visible && <EyeOff className="w-3 h-3 text-gray-500" />}
                                            </button>
                                        ))}
                                        {filteredLayers.length === 0 && (
                                            <div className={`px-3 py-4 text-[10px] text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                {showOnlyFinish ? 'BOQ 마감 레이어 없음' : '레이어 없음'}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* ── Properties Panel ── */}
                        <AnimatePresence>
                            {showProperties && (
                                <motion.div
                                    initial={{ width: 0, opacity: 0 }} animate={{ width: 260, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
                                    className={`border-l overflow-hidden flex flex-col shrink-0 ${isDark ? 'bg-gray-900/90 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                                >
                                    <div className={`px-3 py-2 border-b text-[11px] font-bold shrink-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                        속성 (Properties)
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-3 min-h-0">
                                        {selectedEntity ? (
                                            <div className="space-y-3">
                                                {/* Type */}
                                                <PropSection title="엔티티 타입" isDark={isDark}>
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                                        {ENTITY_TYPE_LABELS[selectedEntity.entity.type] || selectedEntity.entity.type}
                                                    </span>
                                                </PropSection>

                                                {/* Layer */}
                                                <PropSection title="레이어" isDark={isDark}>
                                                    <span className="flex items-center gap-1.5">
                                                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: getACIColor(selectedEntity.entity.color || 7) }} />
                                                        <span className="text-[10px] font-mono">{selectedEntity.entity.layer || '0'}</span>
                                                    </span>
                                                    {(() => {
                                                        const cls = classifyLayerCategory(selectedEntity.entity.layer || '');
                                                        return cls.isFinish ? (
                                                            <span className="ml-2 text-[9px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-400">{cls.category}</span>
                                                        ) : null;
                                                    })()}
                                                </PropSection>

                                                {/* Color */}
                                                <PropSection title="색상" isDark={isDark}>
                                                    <span className="flex items-center gap-1.5">
                                                        <span className="w-4 h-4 rounded border" style={{ backgroundColor: getACIColor(selectedEntity.entity.color || 7), borderColor: isDark ? '#333' : '#ddd' }} />
                                                        <span className="text-[10px] font-mono">Index {selectedEntity.entity.color || 7}</span>
                                                    </span>
                                                </PropSection>

                                                {/* Position */}
                                                {selectedEntity.entity.startPos && (
                                                    <PropSection title="위치" isDark={isDark}>
                                                        <span className="text-[10px] font-mono">
                                                            ({selectedEntity.entity.startPos.x.toFixed(1)}, {selectedEntity.entity.startPos.y.toFixed(1)})
                                                        </span>
                                                    </PropSection>
                                                )}
                                                {selectedEntity.entity.center && (
                                                    <PropSection title="중심점" isDark={isDark}>
                                                        <span className="text-[10px] font-mono">
                                                            ({selectedEntity.entity.center.x.toFixed(1)}, {selectedEntity.entity.center.y.toFixed(1)})
                                                        </span>
                                                    </PropSection>
                                                )}

                                                {/* Dimensions */}
                                                {selectedEntity.entity.radius != null && selectedEntity.entity.radius > 0 && (
                                                    <PropSection title="반지름" isDark={isDark}>
                                                        <span className="text-[10px] font-mono">{selectedEntity.entity.radius.toFixed(2)}</span>
                                                    </PropSection>
                                                )}
                                                {selectedEntity.entity.startPoint && selectedEntity.entity.endPoint && (
                                                    <PropSection title="길이" isDark={isDark}>
                                                        <span className="text-[10px] font-mono">
                                                            {Math.hypot(selectedEntity.entity.endPoint.x - selectedEntity.entity.startPoint.x, selectedEntity.entity.endPoint.y - selectedEntity.entity.startPoint.y).toFixed(2)}
                                                        </span>
                                                    </PropSection>
                                                )}
                                                {selectedEntity.entity.vertices && selectedEntity.entity.vertices.length > 1 && (
                                                    <PropSection title={`정점 (${selectedEntity.entity.vertices.length})`} isDark={isDark}>
                                                        <div className="max-h-24 overflow-y-auto">
                                                            {selectedEntity.entity.vertices.map((v, i) => (
                                                                <div key={i} className="text-[9px] font-mono opacity-70">
                                                                    [{i}] ({v.x.toFixed(1)}, {v.y.toFixed(1)})
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </PropSection>
                                                )}

                                                {/* Text */}
                                                {selectedEntity.entity.text && (
                                                    <PropSection title="텍스트" isDark={isDark}>
                                                        <span className="text-[10px] break-all">{selectedEntity.entity.text}</span>
                                                    </PropSection>
                                                )}

                                                {/* Block name */}
                                                {selectedEntity.entity.blockName && (
                                                    <PropSection title="블록명" isDark={isDark}>
                                                        <span className="text-[10px] font-mono">{selectedEntity.entity.blockName}</span>
                                                    </PropSection>
                                                )}

                                                {/* BBox */}
                                                {selectedEntity.entity.bbox && (
                                                    <PropSection title="Bounding Box" isDark={isDark}>
                                                        <div className="text-[9px] font-mono opacity-70 space-y-0.5">
                                                            <div>Min: ({selectedEntity.entity.bbox.minX.toFixed(1)}, {selectedEntity.entity.bbox.minY.toFixed(1)})</div>
                                                            <div>Max: ({selectedEntity.entity.bbox.maxX.toFixed(1)}, {selectedEntity.entity.bbox.maxY.toFixed(1)})</div>
                                                            <div>Size: {(selectedEntity.entity.bbox.maxX - selectedEntity.entity.bbox.minX).toFixed(1)} × {(selectedEntity.entity.bbox.maxY - selectedEntity.entity.bbox.minY).toFixed(1)}</div>
                                                        </div>
                                                    </PropSection>
                                                )}

                                                {/* ID */}
                                                <PropSection title="Entity ID" isDark={isDark}>
                                                    <span className="text-[10px] font-mono opacity-50">#{selectedEntity.entity.id}</span>
                                                </PropSection>
                                            </div>
                                        ) : (
                                            <div className={`text-[10px] text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                                <MousePointer2 className="w-6 h-6 mx-auto mb-2 opacity-30" />
                                                <p>엔티티를 선택하면</p>
                                                <p>속성 정보가 표시됩니다</p>
                                                <p className="mt-2 opacity-50">Select 도구로 클릭하세요</p>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ═══════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════

function PropSection({ title, isDark, children }: { title: string; isDark: boolean; children: React.ReactNode }) {
    return (
        <div>
            <div className={`text-[9px] font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{title}</div>
            <div className={`text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{children}</div>
        </div>
    );
}

// ── Geometry helpers ──

/** Shoelace formula for polygon area */
function polygonArea(vertices: Point2D[]): number {
    let area = 0;
    for (let i = 0; i < vertices.length; i++) {
        const j = (i + 1) % vertices.length;
        area += vertices[i].x * vertices[j].y;
        area -= vertices[j].x * vertices[i].y;
    }
    return Math.abs(area) / 2;
}

/** Point-in-polygon (ray casting) */
function pointInPolygon(pt: Point2D, polygon: Point2D[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;
        if (((yi > pt.y) !== (yj > pt.y)) &&
            (pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
}
